import { useState } from 'react'
import { Calculator, Award, CheckCircle, XCircle, Info } from 'lucide-react'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface CandidateSkill {
  uuid?: string
  skill_id?: number
  skill_name?: string
  skillName?: string
  name?: string
  proficiency_level?: string
  proficiencyLevel?: string
  category?: string
  skillCategory?: string
  isPrimarySkill?: boolean
  yearsOfExperience?: number
  certificationRelated?: boolean
}

interface CandidateSkillScoreProps {
  candidateSkills: CandidateSkill[] | any
  positionSkills: PositionSkill[]
  className?: string
}

const CandidateSkillScore = ({ candidateSkills, positionSkills, className = '' }: CandidateSkillScoreProps) => {
  const [showDetails, setShowDetails] = useState(false)

  // Normalize candidate skills to array
  const normalizedCandidateSkills: CandidateSkill[] = Array.isArray(candidateSkills) 
    ? candidateSkills 
    : candidateSkills?.skills 
    ? candidateSkills.skills 
    : []

  // Calculate skill match score
  const calculateSkillScore = () => {
    if (!positionSkills || positionSkills.length === 0) {
      return { score: 0, percentage: 0, details: { required: [], goodToHave: [], matched: [], missing: [] } }
    }

    const requiredSkills = positionSkills.filter(skill => !skill.is_good_to_have)
    const goodToHaveSkills = positionSkills.filter(skill => skill.is_good_to_have)
    
    let totalPossiblePoints = (requiredSkills.length * 5) + (goodToHaveSkills.length * 1)
    let earnedPoints = 0
    
    const matchedSkills: PositionSkill[] = []
    const missingSkills: PositionSkill[] = []

    // Check required skills (5 points each)
    requiredSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        const candidateSkillName = candSkill.skillName || candSkill.skill_name || candSkill.name;
        return candidateSkillName?.toLowerCase() === posSkill.skill_name?.toLowerCase() ||
               candSkill.skill_id === posSkill.skill_id;
      })
      
      if (hasSkill) {
        earnedPoints += 5
        matchedSkills.push(posSkill)
      } else {
        missingSkills.push(posSkill)
      }
    })

    // Check good-to-have skills (1 point each)
    goodToHaveSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        const candidateSkillName = candSkill.skillName || candSkill.skill_name || candSkill.name;
        return candidateSkillName?.toLowerCase() === posSkill.skill_name?.toLowerCase() ||
               candSkill.skill_id === posSkill.skill_id;
      })
      
      if (hasSkill) {
        earnedPoints += 1
        matchedSkills.push(posSkill)
      } else {
        missingSkills.push(posSkill)
      }
    })

    const percentage = totalPossiblePoints > 0 ? Math.round((earnedPoints / totalPossiblePoints) * 100) : 0

    return {
      score: earnedPoints,
      totalPossible: totalPossiblePoints,
      percentage,
      details: {
        required: requiredSkills,
        goodToHave: goodToHaveSkills,
        matched: matchedSkills,
        missing: missingSkills
      }
    }
  }

  const skillScore = calculateSkillScore()

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50'
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  const getScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <Award className="w-4 h-4" />
    if (percentage >= 60) return <CheckCircle className="w-4 h-4" />
    return <XCircle className="w-4 h-4" />
  }

  return (
    <div className={`relative ${className}`}>
      {/* Score Display */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getScoreColor(skillScore.percentage)}`}
          title="Click to see score calculation"
        >
          {getScoreIcon(skillScore.percentage)}
          <span className="ml-1">{skillScore.percentage}%</span>
          <Calculator className="w-3 h-3 ml-1 opacity-60" />
        </button>
      </div>

      {/* Score Details Popup */}
      {showDetails && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Skill Score Calculation</h4>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Score Summary */}
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex justify-between text-sm">
                  <span>Points Earned:</span>
                  <span className="font-medium">{skillScore.score}/{skillScore.totalPossible}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Percentage:</span>
                  <span className="font-medium">{skillScore.percentage}%</span>
                </div>
              </div>

              {/* Scoring System */}
              <div className="text-xs text-gray-600">
                <div className="flex items-center space-x-1 mb-1">
                  <Info className="w-3 h-3" />
                  <span>Scoring: Required skills = 5 pts, Good-to-have = 1 pt</span>
                </div>
              </div>

              {/* Matched Skills */}
              {skillScore.details.matched.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-green-700 mb-2">
                    ✓ Matched Skills ({skillScore.details.matched.length})
                  </h5>
                  <div className="space-y-1">
                    {skillScore.details.matched.map((skill, index) => (
                      <div key={index} className="flex justify-between text-xs">
                        <span className="text-gray-700">{skill.skill_name}</span>
                        <span className="text-green-600 font-medium">
                          +{skill.is_good_to_have ? 1 : 5} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Skills */}
              {skillScore.details.missing.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-red-700 mb-2">
                    ✗ Missing Skills ({skillScore.details.missing.length})
                  </h5>
                  <div className="space-y-1">
                    {skillScore.details.missing.map((skill, index) => (
                      <div key={index} className="flex justify-between text-xs">
                        <span className="text-gray-700">{skill.skill_name}</span>
                        <span className="text-red-600">
                          {skill.is_good_to_have ? 'Good-to-have' : 'Required'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {positionSkills.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-2">
                  No position skills defined for comparison
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CandidateSkillScore